// src/pages/Notifications/NotificationList.jsx
// -----------------------------------------------------------------------------
// In-app notification center (Requirement #22: All stakeholders receive
// in-app notifications for each action). Lets the user view, filter, mark read,
// and delete notifications.
// -----------------------------------------------------------------------------
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import Loader from '../../components/common/Loader';

export default function NotificationList() {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch notifications on mount to ensure fresh status
  useEffect(() => {
    fetchNotifications();
  }, []);

  const getNotificationIcon = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('password')) return '🔑';
    if (t.includes('assign') || t.includes('team')) return '👤';
    if (t.includes('create') || t.includes('new')) return '➕';
    if (t.includes('update') || t.includes('change') || t.includes('status')) return '📝';
    if (t.includes('comment')) return '💬';
    return '🔔';
  };

  const getNotificationBadgeClass = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('password')) return 'badge-warning';
    if (t.includes('assign')) return 'badge-primary';
    if (t.includes('create')) return 'badge-success';
    if (t.includes('comment')) return 'badge-info';
    return 'badge-secondary';
  };

  const handleNotificationClick = async (n, e) => {
    // If they clicked the delete button, don't trigger the click behavior
    if (e.target.closest('.btn-delete-notif')) return;

    const id = n.notificationId ?? n.notificationID ?? n.NotificationId ?? n.NotificationID;
    const isRead = n.isReadFlag ?? n.IsReadFlag ?? n.isRead ?? n.IsRead;
    const ticketId = n.ticketId ?? n.ticketID ?? n.TicketId ?? n.TicketID;

    if (!isRead && id) {
      await markAsRead(id);
    }

    if (ticketId) {
      navigate(`/tickets/${ticketId}`);
    }
  };

  // Filter notifications
  const filteredNotifications = notifications.filter((n) => {
    const isRead = n.isReadFlag ?? n.IsReadFlag ?? n.isRead ?? n.IsRead;
    
    // Status filter
    if (filter === 'unread' && isRead) return false;
    if (filter === 'read' && !isRead) return false;

    // Search query filter
    if (searchQuery) {
      const msg = (n.message ?? n.Message ?? '').toLowerCase();
      const type = (n.notificationType ?? n.NotificationType ?? '').toLowerCase();
      return msg.includes(searchQuery.toLowerCase()) || type.includes(searchQuery.toLowerCase());
    }

    return true;
  });

  if (loading && notifications.length === 0) {
    return <Loader label="Loading notifications..." />;
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div className="page-header">
        <div>
          <h1>Notifications</h1>
          <div className="subtitle">
            You have {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}.
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            className="btn btn-outline" 
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
          >
            Mark all as read
          </button>
        </div>
      </div>

      {/* Toolbar / Filters */}
      <div className="card table-toolbar" style={{ padding: '16px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="filters" style={{ display: 'flex', gap: '8px' }}>
          <button
            className={`btn btn-small-padding ${filter === 'all' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setFilter('all')}
            style={{ borderRadius: '20px' }}
          >
            All
          </button>
          <button
            className={`btn btn-small-padding ${filter === 'unread' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setFilter('unread')}
            style={{ borderRadius: '20px' }}
          >
            Unread {unreadCount > 0 && <span style={{ marginLeft: '4px', background: 'rgba(255,255,255,0.2)', padding: '2px 6px', borderRadius: '10px', fontSize: '10px' }}>{unreadCount}</span>}
          </button>
          <button
            className={`btn btn-small-padding ${filter === 'read' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setFilter('read')}
            style={{ borderRadius: '20px' }}
          >
            Read
          </button>
        </div>

        <div className="search-input-wrapper">
          <input
            type="text"
            placeholder="Filter notifications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
            style={{ borderRadius: '20px', width: '220px' }}
          />
          <span className="search-icon">🔍</span>
        </div>
      </div>

      {/* Notifications List */}
      <div className="card" style={{ padding: '0px', overflow: 'hidden' }}>
        {filteredNotifications.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>🎉</span>
            <h3 style={{ margin: '0 0 8px 0', fontWeight: '700' }}>All Caught Up!</h3>
            <p className="text-muted" style={{ margin: '0' }}>
              {filter === 'unread' 
                ? 'No unread notifications matching your filters.' 
                : filter === 'read' 
                  ? 'No read notifications found.'
                  : 'You have no notifications at the moment.'}
            </p>
          </div>
        ) : (
          filteredNotifications.map((n) => {
            const id = n.notificationId ?? n.notificationID ?? n.NotificationId ?? n.NotificationID;
            const isRead = n.isReadFlag ?? n.IsReadFlag ?? n.isRead ?? n.IsRead;
            const created = n.createdDttm ?? n.CreatedDttm;
            const msg = n.message ?? n.Message;
            const type = n.notificationType ?? n.NotificationType;

            return (
              <div
                key={id}
                onClick={(e) => handleNotificationClick(n, e)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px 20px',
                  borderBottom: '1px solid var(--color-border)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  backgroundColor: isRead ? 'transparent' : 'rgba(234, 88, 12, 0.04)',
                  borderLeft: isRead ? '4px solid transparent' : '4px solid var(--afix-orange)',
                }}
                className="notification-item"
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isRead ? 'rgba(0,0,0,0.02)' : 'rgba(234, 88, 12, 0.06)' }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isRead ? 'transparent' : 'rgba(234, 88, 12, 0.04)' }}
              >
                {/* Icon Container */}
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '12px',
                    backgroundColor: isRead ? 'rgba(148, 163, 184, 0.1)' : 'rgba(234, 88, 12, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    marginRight: '16px',
                    flexShrink: 0,
                  }}
                >
                  {getNotificationIcon(type)}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0, paddingRight: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <span 
                      className={`badge ${getNotificationBadgeClass(type)}`} 
                      style={{ fontSize: '10px', textTransform: 'uppercase', padding: '2px 8px' }}
                    >
                      {type}
                    </span>
                    {!isRead && (
                      <span 
                        style={{ 
                          width: '6px', 
                          height: '6px', 
                          borderRadius: '50%', 
                          backgroundColor: 'var(--afix-orange)', 
                          display: 'inline-block' 
                        }} 
                      />
                    )}
                  </div>
                  <p style={{ margin: '0', fontSize: '13.5px', fontWeight: isRead ? '500' : '650', color: 'var(--color-text)', wordBreak: 'break-word', lineHeight: '1.4' }}>
                    {msg}
                  </p>
                  <span className="text-muted" style={{ fontSize: '11px', marginTop: '4px', display: 'inline-block' }}>
                    {new Date(created).toLocaleString()}
                  </span>
                </div>

                {/* Actions (Delete button) */}
                <button
                  className="btn-delete-notif"
                  onClick={() => deleteNotification(id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-text-muted)',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-danger)'; e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.08)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; e.currentTarget.style.backgroundColor = 'transparent' }}
                  title="Delete notification"
                >
                  🗑️
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}